<template>
  <div class="cat-moneda">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-moneda-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${monedas.length} Registros`"
          @new="addMoneda"
          @refresh="loadMonedas"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando Monedas...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!monedas || monedas.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay monedas registradas. Haga clic en 'Nuevo' para agregar una.
          </div>
        </div>
        
        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && monedas && monedas.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="monedas"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar Monedas..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editMoneda(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar Moneda"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar Moneda"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Moneda -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="monedaModal" 
      tabindex="-1" 
      aria-labelledby="monedaModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="monedaModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Moneda' : 'Nueva Moneda' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatMonedaForm
              :moneda="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatMonedaService from '../controllers/CatMonedaController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatMonedaForm from '../components/CatMonedaForm.vue';

export default {
  name: 'CatMonedaView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatMonedaForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatMonedaService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        Simbolo: '',
        Clave2: '',
        Clave3: '',
        Decimales: 2,
        Orden: 0,
        Notas: ''
      }
    };
  },
  computed: {
    monedas() {
      return this.service.monedas;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'Empresa ID', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'Simbolo', label: 'Símbolo', sortable: true },
        { key: 'Clave2', label: 'Clave 2', sortable: true },
        { key: 'Clave3', label: 'Clave 3', sortable: true },
        { key: 'Decimales', label: 'Decimales', sortable: true },
        { key: 'Orden', label: 'Orden', sortable: true },
        { key: 'EsActivo', label: 'Activo', sortable: true },
        { key: 'Notas', label: 'Notas', sortable: true }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadMonedas() {
      console.log('Cargando Monedas...');
      await this.service.listar();
    },
    addMoneda() {
      this.isEditing = false;
      this.openModal();
    },
    async editMoneda(moneda) {
      this.isEditing = true;
      try {
        // Cargar datos completos de la Moneda
        const response = await this.service.lectura(moneda.Clave);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...moneda };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar Moneda:', error);
        this.$alert.error('No se pudo cargar la Moneda para edición.');
      }
    },
    confirmDelete(clave) {
      console.log('Iniciando confirmación de eliminación para:', clave);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar la Moneda "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteMoneda(clave);
        }
      });
    },
    async deleteMoneda(clave) {
      console.log('Ejecutando eliminación para:', clave);
      
      try {
        const response = await this.service.borrar({ Clave: clave });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Moneda eliminada exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar la Moneda. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar Moneda:', error);
        this.$alert.error('No se pudo eliminar la Moneda. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        Simbolo: '',
        Clave2: '',
        Clave3: '',
        Decimales: 2,
        Orden: 0,
        Notas: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Moneda guardada exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar la Moneda. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar Moneda:', error);
        this.$alert.error('No se pudo guardar la Moneda. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-moneda {
  min-height: calc(100vh - 200px);
}

.cat-moneda-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>